import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import BulkActionBar, { BulkActionItem } from "components/BulkActionBar";
import { useNotification } from "components/NotificationProvider";
import FilterDialog from "components/personnelAndFleet/common/FilterDialog";
import FilterFields, { FilterOption } from "components/personnelAndFleet/common/FilterFields";
import FleetCardList from "components/personnelAndFleet/fleet/FleetCardList";
import FleetFilter from "components/personnelAndFleet/fleet/FleetFilter";
import UpsertVehicleModal from "components/personnelAndFleet/fleet/UpsertVehicleModal";

import { useAuth } from "@sal/authentication.react";
import {
	DEFAULT_FLEET_STATUS_FILTER,
	FLEET_STATUS_FILTER_OPTIONS,
	FLEET_STATUS_LABELS,
	FleetStatusFilterOption,
	FleetVehicleStatus
} from "enums/FleetEnums";
import {
	bulkActivateFleetVehiclesApi,
	bulkDeactivateFleetVehiclesApi,
	useActivateFleetVehicle,
	useDeactivateFleetVehicle,
	useGetFleetVehicles
} from "hooks/fleetVehicles";
import { useBreakpoint } from "hooks/useBreakpoint";
import { useFleetQueryParams } from "hooks/useFleetQueryParams";
import { FleetVehicle } from "models/FleetVehicle";
import { useEffect, useMemo, useState } from "react";
import FleetDataTable from "./FleetDataTable";

export default function FleetTab() {
	const theme = useTheme();
	const auth = useAuth();
	const canWrite = auth.userHasPermission("Fleet", "Write");
	const { isMobile, isTablet } = useBreakpoint();
	const { fleetStatus, setFleetStatus, searchFilter, setSearchFilter, applyFilters } =
		useFleetQueryParams();
	const [modalOpen, setModalOpen] = useState(false);
	const [vehicleToEdit, setVehicleToEdit] = useState<FleetVehicle | null>(null);
	const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
	const [bulkLoading, setBulkLoading] = useState<"deactivate" | "activate" | null>(null);
	const [filterModalOpen, setFilterModalOpen] = useState(false);
	const [pendingFilters, setPendingFilters] = useState({
		status: fleetStatus as string,
		search: searchFilter
	});
	const { data: vehicles, isDataLoading, dataError, mutateData } = useGetFleetVehicles();
	const { deactivateFleetVehicle } = useDeactivateFleetVehicle();
	const { activateFleetVehicle } = useActivateFleetVehicle();
	const { showNotification } = useNotification();

	useEffect(() => {
		if (dataError) {
			showNotification({ message: "Unable to load fleet vehicles", severity: "error" });
		}
	}, [dataError, showNotification]);

	const filteredRows = useMemo(() => {
		let filtered = vehicles;

		// Filter by status
		if (fleetStatus !== FLEET_STATUS_FILTER_OPTIONS.ALL_FLEET) {
			filtered = filtered.filter((vehicle) => vehicle.status === fleetStatus);
		}

		// Filter by search
		if (searchFilter) {
			const query = searchFilter.toLowerCase();
			filtered = filtered.filter(
				(vehicle) =>
					vehicle.registration?.toLowerCase().includes(query) ||
					vehicle.tractorRego?.toLowerCase().includes(query) ||
					vehicle.trailer1Rego?.toLowerCase().includes(query) ||
					vehicle.trailer2Rego?.toLowerCase().includes(query)
			);
		}

		return filtered;
	}, [vehicles, fleetStatus, searchFilter]);

	const modalVehicle = useMemo(() => {
		if (!vehicleToEdit) {
			return null;
		}

		return {
			id: vehicleToEdit.id,
			registration: vehicleToEdit.registration,
			type: vehicleToEdit.type,
			lengthM: String(vehicleToEdit.lengthM ?? ""),
			refrigerated:
				typeof vehicleToEdit.refrigerated === "boolean"
					? vehicleToEdit.refrigerated
						? "yes"
						: "no"
					: String(vehicleToEdit.refrigerated ?? ""),
			driverContactId: vehicleToEdit.driverContactId ?? null,
			defaultDriver: vehicleToEdit.defaultDriver,
			driverActive: vehicleToEdit.driverActive,
			tractorRego: vehicleToEdit.tractorRego ?? "",
			trailer1Rego: vehicleToEdit.trailer1Rego ?? "",
			trailer2Rego: vehicleToEdit.trailer2Rego ?? "",
			status: String(vehicleToEdit.status ?? "")
		};
	}, [vehicleToEdit]);

	const handleOpenCreate = () => {
		setVehicleToEdit(null);
		setModalOpen(true);
	};

	const handleOpenEdit = (vehicle: FleetVehicle) => {
		setVehicleToEdit(vehicle);
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setVehicleToEdit(null);
	};

	const handleDuplicateVehicle = (vehicle: FleetVehicle) => {
		const duplicatedVehicle: FleetVehicle = {
			id: 0,
			registration: "",
			type: vehicle.type,
			lengthM: vehicle.lengthM,
			refrigerated: vehicle.refrigerated,
			status: vehicle.status
		};
		setVehicleToEdit(duplicatedVehicle);
		setModalOpen(true);
	};

	const handleDeactivateVehicle = async (vehicle: FleetVehicle) => {
		const result = await deactivateFleetVehicle(vehicle.id);
		if (result.success) {
			showNotification({ message: "Vehicle deactivated successfully", severity: "success" });
		} else {
			showNotification({ message: result.errorMessage, severity: "error" });
		}
	};

	const handleActivateVehicle = async (vehicle: FleetVehicle) => {
		const result = await activateFleetVehicle(vehicle.id);
		if (result.success) {
			showNotification({ message: "Vehicle activated successfully", severity: "success" });
		} else {
			showNotification({ message: result.errorMessage, severity: "error" });
		}
	};

	const selectedVehicles = useMemo(
		() => vehicles.filter((v) => selectedRows.has(v.id)),
		[vehicles, selectedRows]
	);

	const hasSelectedActive = selectedVehicles.some((v) => v.status === FleetVehicleStatus.ACTIVE);
	const hasSelectedDeactivated = selectedVehicles.some(
		(v) => v.status !== FleetVehicleStatus.ACTIVE
	);

	const handleBulkDeactivate = async () => {
		const ids = selectedVehicles
			.filter((v) => v.status === FleetVehicleStatus.ACTIVE)
			.map((v) => v.id);
		setBulkLoading("deactivate");
		try {
			const { succeeded, failed } = await bulkDeactivateFleetVehiclesApi(ids);

			if (succeeded.length > 0) {
				if (failed.length === 0) {
					setSelectedRows(new Set());
				} else {
					setSelectedRows((prev) => {
						const next = new Set(prev);
						succeeded.forEach((id) => next.delete(id));
						return next;
					});
				}
				mutateData();
			}

			if (failed.length === 0) {
				showNotification({
					message:
						succeeded.length === 1
							? "Vehicle deactivated successfully"
							: `${succeeded.length} vehicles deactivated successfully`,
					severity: "success"
				});
			} else if (succeeded.length === 0) {
				showNotification({
					message: "Failed to deactivate vehicles. Please try again.",
					severity: "error"
				});
			} else {
				showNotification({
					message: `${succeeded.length} vehicle${succeeded.length !== 1 ? "s" : ""} deactivated. ${failed.length} failed, please try again.`,
					severity: "warning"
				});
			}
		} catch {
			showNotification({
				message: "An unexpected error occurred. Please try again.",
				severity: "error"
			});
		} finally {
			setBulkLoading(null);
		}
	};

	const handleBulkActivate = async () => {
		const ids = selectedVehicles
			.filter((v) => v.status !== FleetVehicleStatus.ACTIVE)
			.map((v) => v.id);
		setBulkLoading("activate");
		try {
			const { succeeded, failed } = await bulkActivateFleetVehiclesApi(ids);

			if (succeeded.length > 0) {
				if (failed.length === 0) {
					setSelectedRows(new Set());
				} else {
					setSelectedRows((prev) => {
						const next = new Set(prev);
						succeeded.forEach((id) => next.delete(id));
						return next;
					});
				}
				mutateData();
			}

			if (failed.length === 0) {
				showNotification({
					message:
						succeeded.length === 1
							? "Vehicle activated successfully"
							: `${succeeded.length} vehicles activated successfully`,
					severity: "success"
				});
			} else if (succeeded.length === 0) {
				showNotification({
					message: "Failed to activate vehicles. Please try again.",
					severity: "error"
				});
			} else {
				showNotification({
					message: `${succeeded.length} vehicle${succeeded.length !== 1 ? "s" : ""} activated. ${failed.length} failed, please try again.`,
					severity: "warning"
				});
			}
		} catch {
			showNotification({
				message: "An unexpected error occurred. Please try again.",
				severity: "error"
			});
		} finally {
			setBulkLoading(null);
		}
	};

	const fleetStatusOptions = useMemo<FilterOption[]>(
		() =>
			Object.values(FLEET_STATUS_FILTER_OPTIONS).map((s) => ({
				value: s,
				label: FLEET_STATUS_LABELS[s as FleetVehicleStatus] ?? s
			})),
		[]
	);

	const handleOpenFilterModal = () => {
		setPendingFilters({ status: fleetStatus, search: searchFilter });
		setFilterModalOpen(true);
	};

	const handleApplyFilters = () => {
		applyFilters(pendingFilters.status as FleetStatusFilterOption, pendingFilters.search);
		setFilterModalOpen(false);
	};

	const handleResetPending = () =>
		setPendingFilters({ status: DEFAULT_FLEET_STATUS_FILTER, search: "" });

	const handleResetFilters = () => applyFilters(DEFAULT_FLEET_STATUS_FILTER, "");

	const bulkActions: BulkActionItem[] = canWrite
		? [
				...(hasSelectedActive
					? [
							{
								label: selectedRows.size === 1 ? "Deactivate vehicle" : "Deactivate vehicles",
								onClick: handleBulkDeactivate,
								color: "error" as const,
								loading: bulkLoading === "deactivate",
								disabled: bulkLoading !== null
							}
						]
					: []),
				...(hasSelectedDeactivated
					? [
							{
								label: selectedRows.size === 1 ? "Activate vehicle" : "Activate vehicles",
								onClick: handleBulkActivate,
								color: "primary" as const,
								loading: bulkLoading === "activate",
								disabled: bulkLoading !== null
							}
						]
					: [])
			]
		: [];

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				position: "relative"
			}}
		>
			<Box
				sx={{
					flex: "0 0 auto",
					minHeight: 0,
					py: { xs: 2, md: 2.5 },
					px: { xs: 2, md: 3 },
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					backgroundColor: theme.palette.divider,
					borderTop: `1px solid ${theme.palette.grey[500]}`
				}}
			>
				<FleetFilter
					fleetStatus={fleetStatus}
					onFleetStatusChange={setFleetStatus}
					searchFilter={searchFilter}
					onSearchFilterChange={setSearchFilter}
					onAddNew={handleOpenCreate}
					onOpenFilterModal={handleOpenFilterModal}
					onReset={handleResetFilters}
					canWrite={canWrite}
				/>
			</Box>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					backgroundColor: "background.paper"
				}}
			>
				{isDataLoading ? (
					<Box
						sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
					>
						<CircularProgress size={24} />
					</Box>
				) : dataError ? (
					<Box
						sx={{
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							p: 2
						}}
					>
						<Typography color="error.main">Unable to load fleet vehicles.</Typography>
					</Box>
				) : isMobile || isTablet ? (
					<FleetCardList
						rows={filteredRows}
						selectedRows={selectedRows}
						setSelectedRows={setSelectedRows}
						selectionDisabled={bulkLoading !== null}
						onEditVehicle={handleOpenEdit}
						onDuplicateVehicle={handleDuplicateVehicle}
						onDeactivateVehicle={handleDeactivateVehicle}
						onActivateVehicle={handleActivateVehicle}
						canWrite={canWrite}
					/>
				) : (
					<FleetDataTable
						rows={filteredRows}
						selectedRows={selectedRows}
						setSelectedRows={setSelectedRows}
						selectionDisabled={bulkLoading !== null}
						onEditVehicle={handleOpenEdit}
						onDuplicateVehicle={handleDuplicateVehicle}
						onDeactivateVehicle={handleDeactivateVehicle}
						onActivateVehicle={handleActivateVehicle}
						canWrite={canWrite}
					/>
				)}
			</Box>

			<BulkActionBar
				selectedCount={selectedRows.size}
				entityLabel="vehicle"
				entityLabelPlural="vehicles"
				onClear={() => setSelectedRows(new Set())}
				actions={bulkActions}
			/>

			<UpsertVehicleModal open={modalOpen} onClose={handleCloseModal} vehicle={modalVehicle} />
			<FilterDialog
				open={filterModalOpen}
				onClose={() => setFilterModalOpen(false)}
				onReset={handleResetPending}
				onSubmit={handleApplyFilters}
			>
				<FilterFields
					statusValue={pendingFilters.status}
					onStatusChange={(value) => setPendingFilters((prev) => ({ ...prev, status: value }))}
					statusOptions={fleetStatusOptions}
					searchValue={pendingFilters.search}
					onSearchChange={(value) => setPendingFilters((prev) => ({ ...prev, search: value }))}
					searchPlaceholder="Search fleet"
				/>
			</FilterDialog>
		</Box>
	);
}
